import { memo, useState, useCallback } from 'react';
import { PersonaForm } from './PersonaForm';
import { PersonaReadOnly } from './PersonaReadOnly';
import styles from './style/SegmentPersona.module.css';

const DEFAULT_TITLE = 'Segment Persona';
const DEFAULT_DESCRIPTION = 'Define this segment\'s persona by including key details about voice, key behaviors and stats, and which tagged data the assistant should use when responding.';
const DEFAULT_FIELD_LABEL = 'Type or Paste Persona';

type SegmentPersonaProps = {
  /** The current persona text (from server/segment data) */
  persona: string | null | undefined;
  /** 
   * Called when user saves the persona. Should handle the API mutation.
   * Returns a promise that resolves on success (form will close) or rejects on error.
   */
  onSave: (value: string | null) => Promise<void>;
  /** Whether a save operation is currently in progress */
  isSaving?: boolean;
  /** Section title */
  title?: string;
  /** Section description */
  description?: string;
  /** Label for the input field */
  fieldLabel?: string;
  /** Placeholder text for both read-only and form views */
  placeholder?: string;
  /** Height of the markdown editor when in form mode */
  editorHeight?: number;
  /** Whether whitelabel theming is enabled (optional) */
  whitelabelEnabled?: boolean;
  /** Primary color for whitelabel theming (optional) */
  primaryColor?: string;
};

/**
 * SegmentPersona is a wrapper component that manages edit state and renders
 * either the read-only view or the form view for editing a segment's persona.
 * 
 * This component follows the "container/presentational" pattern:
 * - It manages the `isEditing` state
 * - It renders `PersonaReadOnly` when not editing
 * - It renders `PersonaForm` when editing
 * - The actual API mutation is handled by the parent through `onSave` callback
 * 
 * @example
 * ```tsx
 * <SegmentPersona
 *   persona={segment.persona}
 *   onSave={async (value) => {
 *     await updatePersonaMutation.mutateAsync(value);
 *   }}
 *   isSaving={updatePersonaMutation.isPending}
 *   whitelabelEnabled={themingContext?.enabled}
 *   primaryColor={themingContext?.palette?.primary?.main}
 * />
 * ```
 */
const SegmentPersonaComponent = ({
  persona,
  onSave,
  isSaving = false,
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  fieldLabel = DEFAULT_FIELD_LABEL,
  placeholder,
  editorHeight = 250,
  whitelabelEnabled,
  primaryColor,
}: SegmentPersonaProps) => {
  // Edit state lives in this wrapper component
  const [isEditing, setIsEditing] = useState(false);

  // Open the form
  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  // Close the form (on cancel)
  const handleCancel = useCallback(() => {
    setIsEditing(false);
  }, []);

  // Handle save - close form on success
  const handleSave = useCallback(async (value: string | null) => {
    await onSave(value);
    setIsEditing(false);
  }, [onSave]);

  return (
    <div className={styles['persona-section']}>
      <div className={styles['persona-header']}>
        <h2 className={styles['persona-title']}>{title}</h2>
        <p className={styles['persona-description']}>{description}</p>
      </div>
      <div className={styles['persona-field']}>
        <label className={styles['persona-field-label']}>{fieldLabel}</label>
        {isEditing ? (
          <PersonaForm
            initialValue={persona || ''}
            onSave={handleSave}
            onCancel={handleCancel}
            isSaving={isSaving}
            height={editorHeight}
            placeholder={placeholder}
          />
        ) : (
          <PersonaReadOnly
            persona={persona}
            onEdit={handleEdit}
            placeholder={placeholder}
            whitelabelEnabled={whitelabelEnabled}
            primaryColor={primaryColor}
          />
        )}
      </div>
    </div>
  );
};

export const SegmentPersona = memo(SegmentPersonaComponent);
